import React from 'react';
import { CheckCircle, Package, MapPin, Clock, CreditCard, Home, ChevronRight } from 'lucide-react';

const OrderConfirmationModal = ({
  isOpen,
  orderDetails,
  onViewOrders,
  onContinueShopping
}) => {
  if (!isOpen) return null;

  const {
    orderId,
    items,
    totalAmount,
    deliverySlot,
    paymentMethod,
    deliveryAddress,
    estimatedDelivery
  } = orderDetails || {};

  const deliverySlotLabels = {
    morning: { label: 'Morning', time: '6:00 AM - 8:00 AM', icon: '🌅' },
    afternoon: { label: 'Afternoon', time: '2:00 PM - 4:00 PM', icon: '☀️' },
    evening: { label: 'Evening', time: '6:00 PM - 8:00 PM', icon: '🌆' }
  };

  const paymentMethodLabels = {
    cod: { label: 'Cash on Delivery', icon: '💵' },
    upi: { label: 'UPI Payment', icon: '📱' },
    card: { label: 'Card Payment', icon: '💳' },
    wallet: { label: 'Wallet', icon: '👛' }
  };

  const selectedSlot = deliverySlotLabels[deliverySlot] || deliverySlotLabels.morning;
  const selectedPayment = paymentMethodLabels[paymentMethod] || paymentMethodLabels.cod;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-8">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-t-3xl p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle size={48} className="text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Order Placed Successfully!</h2>
          <p className="text-green-100">Thank you for your order</p>
        </div>

        <div className="p-6">
          {/* Order ID */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6 text-center">
            <div className="text-sm text-blue-600 mb-1">Order ID</div>
            <div className="text-2xl font-bold text-blue-800">#{orderId || 'MK12345'}</div>
            <div className="text-xs text-blue-600 mt-2">
              {new Date().toLocaleDateString('en-IN', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Package size={20} className="text-blue-600" />
              Order Items ({items?.length || 0})
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3 max-h-40 overflow-y-auto">
              {items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl">
                      {item.icon || '🥛'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-800">₹{item.price * item.quantity}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Details */}
          <div className="space-y-4 mb-6">
            {/* Delivery Slot */}
            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
              <Clock size={20} className="text-orange-600 mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-gray-800 text-sm mb-1">Delivery Slot</div>
                <div className="text-gray-700 flex items-center gap-2">
                  <span className="text-lg">{selectedSlot.icon}</span>
                  <span className="font-medium">{selectedSlot.label}</span>
                  <span className="text-sm text-gray-500">({selectedSlot.time})</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            {deliveryAddress && (
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
                <MapPin size={20} className="text-purple-600 mt-1" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm mb-1">Delivery Address</div>
                  <p className="text-gray-700 text-sm">
                    {deliveryAddress.address || '123 Main Street, Sector 29'}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {deliveryAddress.city || 'Gurugram'}, {deliveryAddress.state || 'Haryana'} - {deliveryAddress.pincode || '122001'}
                  </p>
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
              <CreditCard size={20} className="text-green-600 mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-gray-800 text-sm mb-1">Payment Method</div>
                <div className="text-gray-700 flex items-center gap-2">
                  <span className="text-lg">{selectedPayment.icon}</span>
                  <span className="font-medium">{selectedPayment.label}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Total */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 mb-6 text-white">
            <div className="flex justify-between items-center mb-2">
              <span className="text-blue-100">Subtotal</span>
              <span className="font-semibold">₹{totalAmount}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-blue-100">Delivery Charges</span>
              <span className="font-semibold text-green-300">FREE</span>
            </div>
            <div className="border-t border-blue-400 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total Paid</span>
                <span className="text-2xl font-bold">₹{totalAmount}</span>
              </div>
            </div>
          </div>

          {/* Estimated Delivery */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6 text-center">
            <div className="text-sm text-yellow-700 mb-1">Estimated Delivery</div>
            <div className="text-lg font-bold text-yellow-800">
              {estimatedDelivery || 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onViewOrders}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
            >
              <Package size={24} />
              Track Your Order
              <ChevronRight size={20} />
            </button>

            <button
              onClick={onContinueShopping}
              className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
            >
              <Home size={24} />
              Continue Shopping
            </button>
          </div>

          {/* Thank You Message */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              We'll send you updates about your order via WhatsApp and Email
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Questions? Contact us at support@milkdelivery.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationModal;
